import React, { useState, useEffect } from 'react';
import { Brain, ChevronDown, X, Printer } from 'lucide-react';
import { sheets, stateById } from '../data/fastBrain';
import SheetLayout from './fastBrain/SheetLayout';
import SheetFields from './fastBrain/SheetFields';
import { WeekStrip } from './fastBrain/DailyCheckIn';
import { MonthPattern } from './fastBrain/CheckInExtras';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const noop = () => {};

const SnapshotModal = ({ share, onClose }) => {
  const sheet = sheets.find((s) => s.id === share.sheet_id);
  if (!sheet) return null;
  const print = () => {
    document.body.classList.add('fb-print-answers');
    window.addEventListener('afterprint', () => document.body.classList.remove('fb-print-answers'), { once: true });
    setTimeout(() => window.print(), 50);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="fb-snapshot-modal">
      <div className="relative bg-[#faf7f2] rounded-2xl max-w-5xl w-full my-8 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="fb-no-print flex items-center justify-end gap-2 mb-2">
          <button onClick={print} className="inline-flex items-center gap-1.5 text-sm font-body text-[#6b3a5e] border border-[#d9cbd6] rounded-full px-3 py-1.5" data-testid="fb-snapshot-print"><Printer size={14} /> Print</button>
          <button onClick={onClose} className="p-2 text-stone-500 hover:text-forest" aria-label="Close" data-testid="fb-snapshot-close"><X size={18} /></button>
        </div>
        <div id="fb-answers-print" className="pointer-events-none select-text">
          <p className="font-body text-xs uppercase tracking-[0.3em] text-stone-500">Brain state: <span className="font-semibold" style={{ color: stateById(sheet.state).color }}>{stateById(sheet.state).name}</span> · shared {fmt(share.shared_at)}</p>
          <h2 className="font-heading text-3xl md:text-4xl text-forest mt-1 mb-6">{sheet.title}</h2>
          {sheet.layout ? <SheetLayout sheet={sheet} data={share.data || {}} onChange={noop} readOnly /> : <SheetFields sheet={sheet} data={share.data || {}} onChange={noop} />}
        </div>
      </div>
    </div>
  );
};

export default function ClientFastBrainPanel({ auth, clientId }) {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    const qs = `admin_email=${encodeURIComponent(auth.email)}&admin_password=${encodeURIComponent(auth.password)}&user_id=${encodeURIComponent(clientId)}`;
    fetch(`${API}/admin/fast-brain/client?${qs}`).then((r) => (r.ok ? r.json() : { shares: [], checkins: [] }))
      .then(setInfo).catch(() => setInfo({ shares: [], checkins: [] }));
  }, [auth.email, auth.password, clientId]);

  if (!info || (!info.shares.length && !info.checkins.length)) return null;
  return (
    <div className="mb-6 bg-white border border-[#e2d4bf] rounded-xl" data-testid="client-fastbrain-panel">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid="client-fastbrain-toggle">
        <Brain size={16} className="text-[#6b3a5e]" />
        <span className="flex-1 font-heading text-sm text-forest">The Spicy Brain Survival Guide</span>
        <span className="font-body text-xs text-stone-500" data-testid="client-fastbrain-count">{info.shares.length} shared sheet{info.shares.length === 1 ? '' : 's'} · {info.checkins.length} check-in{info.checkins.length === 1 ? '' : 's'}</span>
        <ChevronDown size={16} className={`text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4">
          {info.checkins.length > 0 && (
            <div>
              <p className="font-body text-[11px] uppercase tracking-wider text-[#8a6a5a] mb-2">Last 7 days</p>
              <WeekStrip checkins={info.checkins.slice(0, 7)} testId="client-fb-week" />
              <p className="font-body text-[11px] uppercase tracking-wider text-[#8a6a5a] mt-4 mb-2">Last 30 days</p>
              <MonthPattern checkins={info.checkins} testId="client-fb-month" />
              {info.checkins.filter((c) => c.note).slice(0, 5).map((c) => (
                <p key={c.date} className="font-body text-xs text-stone-600 mt-1"><strong style={{ color: stateById(c.state)?.color }}>{c.date} · {stateById(c.state)?.name}:</strong> {c.note}</p>
              ))}
            </div>
          )}
          {info.shares.length > 0 && (
            <ul className="space-y-1.5">
              {info.shares.map((s) => {
                const sh = sheets.find((x) => x.id === s.sheet_id);
                return (
                  <li key={s.id}>
                    <button onClick={() => setActive(s)} className="w-full flex items-center gap-3 text-left border border-stone-100 rounded-lg px-3 py-2 hover:border-[#6b3a5e]/40" data-testid={`client-fb-share-${s.id}`}>
                      <span className="flex-1 font-body text-sm text-forest">{sh?.title || s.sheet_id}</span>
                      <span className="font-body text-xs text-stone-500">{fmt(s.shared_at)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      {active && <SnapshotModal share={active} onClose={() => setActive(null)} />}
    </div>
  );
}
